<template>
  <div class="space-page">
    <!-- 共用头部导航栏 -->
    <header-nav></header-nav>

    <!-- 左右布局：左15%功能区（固定） + 右85%内容区（滚动） -->
    <div class="space-layout">
      <!-- ===== 左侧功能区（固定） ===== -->
      <aside class="space-side">
        <!-- 我的信息卡 -->
        <div class="side-card user-card">
          <div class="side-card-title">🌟 我的空间</div>
          <template v-if="user && user.no">
            <el-upload
                class="avatar-uploader"
                :action="avatarUploadUrl"
                name="file"
                accept="image/*"
                :show-file-list="false"
                :before-upload="beforeAvatarUpload"
                :on-success="onAvatarUploadSuccess"
                :on-error="onAvatarUploadError">
              <el-avatar :size="64" class="user-avatar" :src="user.avatar || null">
                {{ avatarText }}
              </el-avatar>
              <div class="avatar-upload-hint">📷 点击换头像</div>
            </el-upload>
            <div class="user-name">
              {{ user.name }}<span v-if="user.roleId === 0" class="crown">👑</span>
            </div>
            <el-tag size="mini" class="role-tag" :type="roleTagType">{{ roleName }}</el-tag>
            <div class="user-coin">🪙 星裙币：{{ user.coin != null ? user.coin : '—' }}</div>
          </template>
          <template v-else>
            <div class="login-tip">登录后可发布动态、点赞与评论</div>
            <el-button type="primary" size="small" round @click="goLogin">去登录</el-button>
          </template>
        </div>

        <!-- 发布动态入口（独立弹窗） -->
        <el-button type="primary" size="medium" round class="publish-entry"
                   icon="el-icon-edit" @click="openPublish">发布动态</el-button>

        <!-- 动态浏览菜单 -->
        <div class="side-card menu-card">
          <div class="side-card-title">🧭 动态浏览</div>
          <div class="menu-item" :class="{ active: filter === 'all' }" @click="switchFilter('all')">
            <i class="el-icon-connection"></i> 全部动态
          </div>
          <div class="menu-item" :class="{ active: filter === 'mine' }" @click="switchFilter('mine')">
            <i class="el-icon-user"></i> 我的动态
          </div>
          <div class="menu-item" @click="openNote">
            <i class="el-icon-edit-outline"></i> 我的笔记
          </div>
          <div class="menu-item" @click="loadMoments">
            <i class="el-icon-refresh"></i> 刷新动态
          </div>
        </div>
      </aside>

      <!-- ===== 右侧内容显示区（可滚动） ===== -->
      <main class="space-main">
        <div class="feed-head">
          <h2>✨ 裙星动态</h2>
          <span class="feed-count">共 {{ moments.length }} 条</span>
        </div>

        <div v-if="moments.length === 0" class="empty-tip">
          <div class="empty-emoji">🌸</div>
          <p>这里还很安静，快来发布第一条动态吧～</p>
        </div>

        <article v-for="m in moments" :key="m.id" class="moment-card">
          <header class="moment-head">
            <el-avatar :size="44" class="moment-avatar clickable" :src="m.avatar || null"
                       @click.stop="openUserProfile(m)">{{ (m.userName || '星').slice(0, 1) }}</el-avatar>
            <div class="moment-user">
              <div class="moment-name clickable" @click.stop="openUserProfile(m)">
                {{ m.userName }}
                <span v-if="isMe(m)" class="me-tag">我</span>
                <span v-if="roleBadge(m)" class="role-badge" :title="roleTitle(m)">{{ roleBadge(m) }}</span>
              </div>
              <div class="moment-time">{{ formatTime(m.createTime) }}</div>
            </div>
            <div class="moment-ops">
              <!-- 删除入口隐藏为三点菜单 -->
              <el-dropdown v-if="canDelete(m)" trigger="click" @command="() => delMoment(m)">
                <span class="moment-more" @click.stop>···</span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="delete" icon="el-icon-delete">删除动态</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </header>

          <!-- 文字内容不触发弹窗；点击图片为放大预览 -->
          <div class="moment-body">
            <p v-if="m.content" class="moment-content">{{ m.content }}</p>
            <div v-if="imageList(m).length" class="moment-images"
                 :class="'count-' + Math.min(imageList(m).length, 9)">
              <div v-for="(img, i) in imageList(m)" :key="i" class="moment-img-cell" @click.stop>
                <el-image :src="imgUrl(img)" fit="cover" class="moment-img"
                          :preview-src-list="imageList(m).map(x => imgUrl(x))"
                          :initial-index="i"></el-image>
              </div>
            </div>
          </div>

          <!-- 操作栏：浏览 / 点赞 / 评论（评论为卡片内联展开，不弹窗） -->
          <footer class="moment-ops-bar">
            <span class="op-item"><i class="op-icon">👁</i>{{ m.views || 0 }}</span>
            <span class="op-item like-btn" :class="{ liked: m.isLiked }" @click="toggleLike(m)">
              <i class="op-icon">{{ m.isLiked ? '❤️' : '🤍' }}</i>{{ m.likes || 0 }}
            </span>
            <span class="op-item comment-toggle" :class="{ active: expandedId === m.id }"
                  @click="toggleCardComment(m)">
              <i class="op-icon">💬</i>{{ m.commentCount || 0 }}
              <i v-if="expandedId === m.id" class="expand-arrow">▲</i>
              <i v-else class="expand-arrow">▼</i>
            </span>
            <!-- 查看详情：打开详情弹窗（浏览+1） -->
            <span class="op-item detail-link" @click="openDetail(m)">
              <i class="op-icon">📄</i>查看详情
            </span>
          </footer>

          <!-- 卡片内联评论区（点击💬向下展开，不弹窗） -->
          <div v-if="expandedId === m.id" class="card-comment-section">
            <div class="card-comment-head">
              <span class="comment-collapse" @click="expandedId = null">收起 ▲</span>
            </div>
            <comment-section
                :key="'card-' + m.id"
                :moment="m"
                :user="user"
                @count-change="delta => onCommentCountChange(m, delta)"
                @need-login="goLogin">
            </comment-section>
          </div>
        </article>

        <div v-if="moments.length > 0" class="feed-foot">🌸 已经到底啦 🌸</div>
      </main>
    </div>

    <!-- ===== 发布动态弹窗（独立屏幕弹窗） ===== -->
    <el-dialog title="✨ 发布动态" :visible.sync="publishVisible" width="520px" center
               custom-class="pink-dialog">
      <div class="publish-dialog-body">
        <el-input
            v-model="draft.content"
            type="textarea"
            :rows="5"
            maxlength="500"
            show-word-limit
            placeholder="分享此刻的心情与穿搭吧～">
        </el-input>

        <div class="img-grid">
          <div v-for="(img, i) in draft.images" :key="i" class="img-item">
            <el-image :src="imgUrl(img)" fit="cover" class="img-thumb"></el-image>
            <div class="img-remove" @click="removeImg(i)">✕</div>
          </div>
          <el-upload
              v-if="draft.images.length < 9"
              :action="uploadUrl"
              name="file"
              accept="image/*"
              :show-file-list="false"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              class="img-uploader">
            <div class="img-add">＋<span>图片</span></div>
          </el-upload>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="publishVisible = false">取 消</el-button>
        <el-button type="primary" :loading="publishing" @click="publish">发 布</el-button>
      </span>
    </el-dialog>

    <!-- ===== 动态详情弹窗（浏览+1 / 点赞 / 评论） ===== -->
    <el-dialog title="🌸 动态详情" :visible.sync="detailVisible" width="560px"
               custom-class="pink-dialog" @closed="resetDetail">
      <div v-if="detail" class="detail-body">
        <header class="detail-head">
          <el-avatar :size="44" class="moment-avatar clickable" :src="detail.avatar || null"
                     @click.stop="openUserProfile(detail)">{{ (detail.userName || '星').slice(0, 1) }}</el-avatar>
          <div class="moment-user">
            <div class="moment-name clickable" @click.stop="openUserProfile(detail)">
              {{ detail.userName }}
              <span v-if="isMe(detail)" class="me-tag">我</span>
              <span v-if="roleBadge(detail)" class="role-badge" :title="roleTitle(detail)">{{ roleBadge(detail) }}</span>
            </div>
            <div class="moment-time">{{ formatTime(detail.createTime) }}</div>
          </div>
        </header>

        <p v-if="detail.content" class="detail-content">{{ detail.content }}</p>

        <div v-if="imageList(detail).length" class="detail-images">
          <el-image
              v-for="(img, i) in imageList(detail)"
              :key="i"
              :src="imgUrl(img)"
              :preview-src-list="previewList(detail)"
              fit="cover"
              class="detail-img">
          </el-image>
        </div>

        <div class="detail-ops">
          <span class="detail-view">👁 浏览 {{ detail.views || 0 }}</span>
          <span class="detail-like like-btn" :class="{ liked: detail.isLiked }" @click="toggleLike(detail)">
            {{ detail.isLiked ? '❤️' : '🤍' }} 点赞 {{ detail.likes || 0 }}
          </span>
        </div>

        <!-- 评论区（分页/点赞/回复/删除，复用卡片组件） -->
        <comment-section
            v-if="detail"
            :key="'detail-' + detail.id"
            :moment="detail"
            :user="user"
            @count-change="delta => onCommentCountChange(detail, delta)"
            @need-login="goLogin">
        </comment-section>
      </div>
    </el-dialog>

    <!-- ===== 用户资料弹窗（点击动态发布人头像/昵称触发，含打赏） ===== -->
    <el-dialog title="🌸 用户资料" :visible.sync="profileVisible" width="440px"
               custom-class="pink-dialog" @closed="resetProfile">
      <div v-if="profileUser" class="profile-body">
        <div class="profile-head">
          <el-avatar :size="72" class="profile-avatar" :src="profileUser.avatar || null">
            {{ (profileUser.name || '星').slice(0, 1) }}
          </el-avatar>
          <div class="profile-head-info">
            <div class="profile-name">
              {{ profileUser.name }}
              <span v-if="profileUser.roleId === 0" title="超级管理员">👑</span>
              <span v-if="profileUser.roleId === 1" title="管理员">⭐</span>
            </div>
            <div class="profile-no">账号：{{ profileUser.no }}</div>
            <el-tag size="mini" class="profile-role-tag" :type="profileRoleTagType(profileUser)">
              {{ profileRoleName(profileUser) }}
            </el-tag>
          </div>
        </div>

        <div class="profile-grid">
          <div class="profile-item">
            <span class="p-label">性别</span>
            <span class="p-value">{{ sexText(profileUser.sex) }}</span>
          </div>
          <div class="profile-item">
            <span class="p-label">年龄</span>
            <span class="p-value">{{ profileUser.age ? profileUser.age + ' 岁' : '—' }}</span>
          </div>
          <div class="profile-item">
            <span class="p-label">手机号</span>
            <span class="p-value">{{ profileUser.phone || '—' }}</span>
          </div>
          <div class="profile-item">
            <span class="p-label">星裙币</span>
            <span class="p-value coin">🪙 {{ profileUser.coin != null ? profileUser.coin : '—' }}</span>
          </div>
          <div class="profile-item">
            <span class="p-label">账号状态</span>
            <span class="p-value">{{ profileUser.isvalid === 'Y' ? '正常' : '已禁用' }}</span>
          </div>
          <div class="profile-item">
            <span class="p-label">UID</span>
            <span class="p-value">#{{ profileUser.id }}</span>
          </div>
        </div>

        <!-- 打赏区：自己的资料不显示 -->
        <div v-if="!isProfileSelf()" class="tip-section">
          <div class="tip-title">🎁 打赏星友</div>
          <div class="tip-balance">我的余额：🪙 {{ user.coin != null ? user.coin : 0 }}</div>
          <div class="tip-quick">
            <span v-for="amt in [10, 50, 100, 520]" :key="amt"
                  class="tip-chip" :class="{ active: Number(tipAmount) === amt }"
                  @click="tipAmount = amt">{{ amt }} 🪙</span>
          </div>
          <el-input v-model.number="tipAmount" type="number" size="small"
                    :min="1" :max="10000" placeholder="自定义打赏金额">
            <template slot="append">🪙</template>
          </el-input>
          <el-button type="danger" round class="tip-btn"
                     :loading="tiping" :disabled="!tipAmount || tipAmount <= 0"
                     @click="doTip">
            💖 打赏 {{ profileUser.name }}
          </el-button>
        </div>
        <div v-else class="self-tip-tip">这是你自己的账号哦～</div>
      </div>
      <div v-else class="profile-loading">加载中...</div>
    </el-dialog>

    <!-- ===== 我的笔记弹窗（便签墙：可新增/保存/删除，按账号落库到 user_note 表） ===== -->
    <el-dialog title="📝 我的便签墙" :visible.sync="noteVisible" width="820px" top="6vh"
               custom-class="pink-dialog note-wall-dialog" @closed="onNoteClosed">
      <div class="note-wall-body">
        <!-- 写新便签区 -->
        <div class="note-editor">
          <div class="note-editor-title">✏️ 写新便签</div>
          <el-input v-model="noteDraft.title" size="small" maxlength="30"
                    placeholder="便签标题（可留空）" class="note-title-input"></el-input>
          <el-input
              v-model="noteDraft.content"
              type="textarea"
              :rows="4"
              maxlength="500"
              show-word-limit
              placeholder="随手记点什么吧，像便利贴一样～">
          </el-input>
          <div class="note-editor-foot">
            <span class="note-count-hint">共 {{ notes.length }} 条便签</span>
            <el-button type="primary" size="small" round icon="el-icon-check"
                       :loading="noteSaving" @click="addNote">保存便签</el-button>
          </div>
        </div>

        <!-- 便签列表（便签墙） -->
        <div v-if="notes.length === 0" class="note-empty">
          <div class="note-empty-emoji">🌷</div>
          <p>还没有便签，在上面写一条吧～</p>
        </div>
        <div v-else class="note-grid">
          <div v-for="(n, i) in notes" :key="n.id" class="note-sticky"
               :style="stickyStyle(i)">
            <div class="note-sticky-head">
              <span class="note-sticky-title">{{ n.title || '无标题' }}</span>
              <span class="note-sticky-del" title="删除" @click="removeNote(i)">✕</span>
            </div>
            <div class="note-sticky-content">{{ n.content }}</div>
            <div class="note-sticky-time">{{ n.time }}</div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 裙星空间（原"关于我们"页面改造）
 * - 左右布局：左侧 15% 功能区（固定），右侧 85% 内容显示区（上下滚动）
 * - 发布动态为独立屏幕弹窗；数据存入 MySQL（moment 表）
 * - 动态支持：浏览次数（出现在列表即+1，无需点击详情）、点赞/取消点赞、留言评论
 * - 超级管理员可删除任意账号发布的动态；发布者本人可删除自己的动态
 */
import HeaderNav from '../HeaderNav.vue'
import CommentSection from './CommentSection.vue'
import request from '../../api/request'
import {
  listMoments, saveMoment, viewMoment, likeMoment, deleteMoment
} from '../../api/moment'
import { getUserProfile, tipUser, updateAvatar } from '../../api/user'
import { listNotes, saveNote, deleteNote } from '../../api/note'
import { formatTime, pad } from '../../utils/format'
import bus from '../../utils/bus'

const BASE_URL = request.defaults.baseURL

export default {
  name: 'Notes',
  components: { HeaderNav, CommentSection },
  data() {
    return {
      user: {},
      moments: [],
      filter: 'all',          // all 全部动态 / mine 我的动态
      publishVisible: false,
      publishing: false,
      draft: { content: '', images: [] },
      detailVisible: false,
      detail: null,
      // 卡片内联评论（点击💬展开，不弹窗）
      expandedId: null,       // 当前展开评论区的动态ID
      // 用户资料弹窗（点击动态发布人头像/昵称）
      profileVisible: false,
      profileUser: null,      // 被查看的用户资料
      tipAmount: null,        // 打赏金额
      tiping: false,          // 打赏请求中
      // 我的便签墙弹窗（按账号持久化到后端 user_note 表）
      noteVisible: false,
      notes: [],                    // 便签列表 [{id, title, content, time}]
      noteDraft: { title: '', content: '' },  // 正在编辑的新便签
      noteSaving: false
    }
  },
  computed: {
    uploadUrl() {
      return BASE_URL + '/api/upload'
    },
    avatarUploadUrl() {
      return BASE_URL + '/api/uploadAvatar'
    },
    avatarText() {
      return this.user && this.user.name ? this.user.name.slice(0, 1) : '星'
    },
    roleName() {
      if (!this.user) return ''
      if (this.user.roleId === 0) return '超级管理员'
      if (this.user.roleId === 1) return '管理员'
      return '星友'
    },
    roleTagType() {
      if (this.user && this.user.roleId === 0) return 'danger'
      if (this.user && this.user.roleId === 1) return 'warning'
      return 'success'
    }
  },
  created() {
    this.readUser()
    this.loadMoments()
    // 登录/退出后刷新左侧用户信息与动态列表（与头部共用事件）
    bus.$on('music:user-changed', this.onUserChanged)
  },
  beforeDestroy() {
    // 与 Storyshop 同款配对解绑，避免路由切换累积监听与重复请求
    bus.$off('music:user-changed', this.onUserChanged)
  },
  methods: {
    onUserChanged() {
      this.readUser()
      this.loadMoments()
    },
    readUser() {
      const cu = sessionStorage.getItem('CurUser')
      if (cu) {
        try {
          this.user = JSON.parse(cu)
        } catch (e) {
          this.user = {}
        }
      } else {
        this.user = {}
      }
    },
    goLogin() {
      bus.$emit('open-login-drawer')
    },
    checkLogin() {
      if (!this.user || !this.user.no) {
        this.$message.warning('请先登录后再操作')
        this.goLogin()
        return false
      }
      return true
    },

    /* ===== 动态加载 ===== */
    loadMoments() {
      const userId = (this.filter === 'mine' && this.user && this.user.id) ? this.user.id : undefined
      const currentNo = this.user && this.user.no ? this.user.no : undefined
      listMoments(userId, currentNo).then(res => {
        this.moments = res.data || []
        // 只要被浏览（出现在列表）就计一次浏览，无需点击详情
        this.countListViews(this.moments)
      }).catch(err => {
        this.$message.error(err.message || '加载动态失败')
      })
    },
    // 列表浏览计数：本次加载的每条动态浏览 +1（被看到即算一次）
    countListViews(list) {
      if (!list || !list.length) return
      list.forEach(m => {
        viewMoment(m.id).then(res => {
          if (res && res.code === 200) {
            m.views = res.data
          }
        }).catch(() => {})
      })
    },
    switchFilter(f) {
      if (this.filter === f) return
      this.filter = f
      this.loadMoments()
    },

    /* ===== 发布动态（弹窗） ===== */
    openPublish() {
      if (!this.checkLogin()) return
      this.publishVisible = true
    },
    publish() {
      if (!this.draft.content.trim() && this.draft.images.length === 0) {
        this.$message.warning('动态内容或图片至少填写一项')
        return
      }
      this.publishing = true
      saveMoment({
        userId: this.user.id,
        userNo: this.user.no,
        userName: this.user.name,
        content: this.draft.content.trim(),
        images: this.draft.images.join(',')
      }).then(res => {
        if (res.code === 200) {
          this.$message.success('动态发布成功！')
          this.publishVisible = false
          this.draft = { content: '', images: [] }
          this.loadMoments()
        } else {
          this.$message.error(res.msg || '发布失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '发布失败')
      }).finally(() => {
        this.publishing = false
      })
    },

    /* ===== 图片上传 ===== */
    handleUploadSuccess(res) {
      if (res && res.success && res.filePath) {
        this.draft.images.push(res.filePath)
      } else {
        this.$message.error((res && res.message) || '图片上传失败')
      }
    },
    handleUploadError() {
      this.$message.error('图片上传失败，请稍后重试')
    },
    removeImg(i) {
      this.draft.images.splice(i, 1)
    },

    /* ===== 详情弹窗（浏览已在列表出现时计过，打开详情不再重复+1） ===== */
    openDetail(m) {
      this.detail = m
      this.detailVisible = true
    },
    resetDetail() {
      this.detail = null
    },
    /* 评论区计数变化同步（评论新增+1 / 删除-1） */
    onCommentCountChange(m, delta) {
      if (m) {
        m.commentCount = Math.max(0, (m.commentCount || 0) + delta)
      }
    },

    /* ===== 卡片内联评论（展开/收起） ===== */
    toggleCardComment(m) {
      if (this.expandedId === m.id) {
        // 已展开则收起
        this.expandedId = null
        return
      }
      this.expandedId = m.id
    },

    /* ===== 点赞 / 取消点赞 ===== */
    toggleLike(m) {
      if (!this.checkLogin()) return
      likeMoment({
        momentId: m.id,
        userId: this.user.id,
        userNo: this.user.no
      }).then(res => {
        if (res.code === 200) {
          m.likes = res.data.likes
          m.isLiked = res.data.liked
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '操作失败')
      })
    },

    /* ===== 删除动态 ===== */
    canDelete(m) {
      if (!this.user || !this.user.no) return false
      // 超级管理员可删除任意账号发布的动态
      if (this.user.roleId === 0) return true
      // 发布者本人可删除自己的动态
      return this.user.no === m.userNo
    },
    isMe(m) {
      return this.user && this.user.no && this.user.no === m.userNo
    },
    // 账号认证标识：0超级管理员=王冠，1管理员=星星
    roleBadge(m) {
      if (!m || m.userRole === undefined || m.userRole === null) return ''
      if (m.userRole === 0) return '👑'
      if (m.userRole === 1) return '⭐'
      return ''
    },
    roleTitle(m) {
      if (!m || m.userRole === undefined || m.userRole === null) return ''
      if (m.userRole === 0) return '超级管理员认证账号'
      if (m.userRole === 1) return '管理员认证账号'
      return ''
    },
    delMoment(m) {
      this.$confirm(`确定删除「${m.userName}」发布的这条动态吗？`, '删除动态', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
        center: true
      }).then(() => {
        deleteMoment({
          id: m.id,
          operatorNo: this.user.no,
          operatorName: this.user.name,
          operatorRole: this.user.roleId
        }).then(res => {
          if (res.code === 200) {
            this.$message.success('动态已删除')
            if (this.detail && this.detail.id === m.id) {
              this.detailVisible = false
            }
            this.loadMoments()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        }).catch(err => {
          this.$message.error(err.message || '删除失败')
        })
      }).catch(() => {})
    },

    /* ===== 用户资料弹窗（点击动态发布人头像/昵称） ===== */
    openUserProfile(m) {
      if (!m || !m.userNo) {
        return
      }
      this.profileUser = null
      this.tipAmount = null
      this.profileVisible = true
      getUserProfile(m.userNo).then(res => {
        if (res.code === 200) {
          this.profileUser = res.data
        } else {
          this.$message.error(res.msg || '加载用户信息失败')
          this.profileVisible = false
        }
      }).catch(err => {
        this.$message.error(err.message || '加载用户信息失败')
        this.profileVisible = false
      })
    },
    resetProfile() {
      this.profileUser = null
      this.tipAmount = null
    },
    isProfileSelf() {
      return this.user && this.user.no && this.profileUser &&
          this.user.no === this.profileUser.no
    },
    profileRoleName(u) {
      if (!u) return ''
      if (u.roleId === 0) return '超级管理员'
      if (u.roleId === 1) return '管理员'
      return '星友'
    },
    profileRoleTagType(u) {
      if (!u) return ''
      if (u.roleId === 0) return 'danger'
      if (u.roleId === 1) return 'warning'
      return 'success'
    },
    sexText(s) {
      if (s === 1) return '男'
      if (s === 2) return '女'
      return '保密'
    },

    /* ===== 打赏（星裙币转账） ===== */
    doTip() {
      if (!this.checkLogin()) return
      if (!this.profileUser) return
      const amount = Math.floor(Number(this.tipAmount))
      if (!amount || amount <= 0) {
        this.$message.warning('请输入正确的打赏金额')
        return
      }
      this.tiping = true
      tipUser({
        fromNo: this.user.no,
        toNo: this.profileUser.no,
        amount: amount
      }).then(res => {
        if (res.code === 200) {
          this.$message.success(`打赏成功！感谢支持 ${this.profileUser.name} 🌸`)
          // 同步左侧"我的星裙币"显示与本地缓存
          this.user.coin = res.data.fromCoin
          this.syncLocalUserCoin(res.data.fromCoin)
          // 同步弹窗内被打赏者的余额
          this.profileUser.coin = res.data.toCoin
          this.tipAmount = null
        } else {
          this.$message.error(res.msg || '打赏失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '打赏失败')
      }).finally(() => {
        this.tiping = false
      })
    },
    syncLocalUserCoin(coin) {
      try {
        const cu = sessionStorage.getItem('CurUser')
        if (cu) {
          const u = JSON.parse(cu)
          u.coin = coin
          sessionStorage.setItem('CurUser', JSON.stringify(u))
        }
      } catch (e) {
        // 缓存更新失败不影响主流程
      }
    },
    /* ===== 头像上传 ===== */
    beforeAvatarUpload(file) {
      const isImg = /^image\//.test(file.type)
      if (!isImg) {
        this.$message.error('只能上传图片文件')
        return false
      }
      if (file.size / 1024 / 1024 > 2) {
        this.$message.error('头像大小不能超过 2MB')
        return false
      }
      return true
    },
    onAvatarUploadSuccess(res) {
      if (res && res.success && res.filePath) {
        // 上传成功后，把头像地址写回当前用户记录
        updateAvatar({ no: this.user.no, avatar: res.filePath }).then(r => {
          if (r.code === 200) {
            this.user.avatar = res.filePath
            this.syncLocalUser('avatar', res.filePath)
            this.$message.success('头像更新成功 🌸')
          } else {
            this.$message.error(r.msg || '头像保存失败')
          }
        }).catch(err => {
          this.$message.error(err.message || '头像保存失败')
        })
      } else {
        this.$message.error((res && res.message) || '头像上传失败')
      }
    },
    onAvatarUploadError() {
      this.$message.error('头像上传失败，请稍后重试')
    },
    syncLocalUser(key, value) {
      try {
        const cu = sessionStorage.getItem('CurUser')
        if (cu) {
          const u = JSON.parse(cu)
          u[key] = value
          sessionStorage.setItem('CurUser', JSON.stringify(u))
        }
      } catch (e) {
        // ignore
      }
    },

    /* ===== 图片地址与预览 ===== */
    imageList(m) {
      if (!m.images) return []
      return m.images.split(',').filter(s => s && s.trim())
    },
    imgUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//.test(path)) return path
      return BASE_URL + path
    },
    previewList(m) {
      return this.imageList(m).map(p => this.imgUrl(p))
    },

    /* ===== 时间格式化（与 CommentSection 共用 utils/format.js） ===== */
    formatTime,
    pad,

    /* ===== 我的便签墙（按账号持久化到后端 user_note 表） ===== */
    // 拉取当前登录用户的便签列表
    loadNotes() {
      if (!this.user || !this.user.no) {
        this.notes = []
        return
      }
      listNotes(this.user.no).then(res => {
        if (res.code === 200) {
          const list = res.data || []
          // 把后端时间字段格式化成便签墙上展示的 time
          this.notes = list.map(n => ({
            id: n.id,
            title: n.title,
            content: n.content,
            time: this.formatTime(n.createTime)
          }))
        } else {
          this.$message.error(res.msg || '加载便签失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '加载便签失败')
      })
    },
    openNote() {
      // 未登录直接提示并唤起登录抽屉，不打开弹窗
      if (!this.checkLogin()) return
      this.noteDraft = { title: '', content: '' }
      this.noteVisible = true
      this.loadNotes()
    },
    addNote() {
      const content = (this.noteDraft.content || '').trim()
      const title = (this.noteDraft.title || '').trim()
      if (!content && !title) {
        this.$message.warning('写点内容再保存吧～')
        return
      }
      this.noteSaving = true
      saveNote({
        userId: this.user.id,
        userNo: this.user.no,
        title: title,
        content: content
      }).then(res => {
        if (res.code === 200) {
          this.noteDraft = { title: '', content: '' }
          this.$message.success('便签已保存 🌸')
          this.loadNotes()
        } else {
          this.$message.error(res.msg || '保存失败')
        }
      }).catch(err => {
        this.$message.error(err.message || '保存失败')
      }).finally(() => {
        this.noteSaving = false
      })
    },
    removeNote(i) {
      const target = this.notes[i]
      if (!target) return
      this.$confirm('确定删除这条便签吗？', '删除便签', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
        center: true
      }).then(() => {
        deleteNote({ id: target.id, userNo: this.user.no }).then(res => {
          if (res.code === 200) {
            this.notes.splice(i, 1)
            this.$message.success('已删除')
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        }).catch(err => {
          this.$message.error(err.message || '删除失败')
        })
      }).catch(() => {})
    },
    // 让便签墙看起来像便利贴：每条轻微旋转 + 颜色轮换
    stickyStyle(i) {
      const hues = [
        { bg: '#fff9c4', border: '#f7d154' },   // 鹅黄
        { bg: '#ffe4ec', border: '#ff9ec3' },    // 粉色
        { bg: '#e4f7e8', border: '#8fd694' },    // 薄荷绿
        { bg: '#e3f2fd', border: '#90caf9' },    // 浅蓝
        { bg: '#f3e5f5', border: '#ce93d8' }     // 淡紫
      ]
      const c = hues[i % hues.length]
      const rot = ((i % 5) - 2) * 1.2  // -2.4° ~ +2.4°
      return {
        background: c.bg,
        borderColor: c.border,
        transform: 'rotate(' + rot + 'deg)'
      }
    },
    onNoteClosed() {
      // 关闭弹窗无需额外处理，便签已在新增/删除时实时落库
    }
  }
}
</script>

<style scoped>
/* ===== 页面容器：整屏固定，仅右侧内容区滚动 ===== */
.space-page {
  height: 100vh;
  padding-top: 60px; /* 为固定头部导航栏让位 */
  box-sizing: border-box;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 15%, rgba(255, 190, 220, 0.35), transparent 32%),
    radial-gradient(circle at 88% 10%, rgba(255, 215, 235, 0.4), transparent 30%),
    radial-gradient(circle at 85% 85%, rgba(255, 205, 225, 0.3), transparent 28%),
    linear-gradient(165deg, #fff2f8, #ffe6f0 45%, #fdeef6);
}

.space-layout {
  display: flex;
  height: 100%;
}

/* ===== 左侧功能区：15%，固定不随内容滚动 ===== */
.space-side {
  width: 15%;
  min-width: 230px;
  flex-shrink: 0;
  padding: 18px 14px 24px;
  box-sizing: border-box;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-right: 1px solid rgba(255, 190, 220, 0.5);
  background: rgba(255, 255, 255, 0.32);
}

.side-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #ffd9e8;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(217, 93, 139, 0.12);
  padding: 16px;
}

.side-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #c9578b;
  letter-spacing: 1px;
  margin-bottom: 12px;
}

/* 我的信息卡 */
.user-card {
  text-align: center;
}

.user-card .side-card-title {
  text-align: left;
}

.user-avatar {
  background: linear-gradient(135deg, #ff9ec3, #f0619c);
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(240, 97, 156, 0.35);
}

.avatar-uploader {
  display: block;
  text-align: center;
  cursor: pointer;
}

.avatar-uploader .user-avatar {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.avatar-uploader:hover .user-avatar {
  transform: scale(1.06);
  box-shadow: 0 6px 18px rgba(240, 97, 156, 0.55);
}

.avatar-upload-hint {
  margin-top: 8px;
  font-size: 11px;
  color: #c78ca6;
  letter-spacing: 1px;
}

.user-name {
  margin-top: 10px;
  font-size: 17px;
  font-weight: 700;
  color: #b84f80;
}

.crown {
  margin-left: 4px;
}

.role-tag {
  margin-top: 8px;
}

.user-coin {
  margin-top: 10px;
  font-size: 13px;
  color: #a06b83;
  background: #fff0f7;
  border-radius: 20px;
  padding: 4px 0;
}

.login-tip {
  font-size: 13px;
  color: #a06b83;
  line-height: 1.7;
  margin-bottom: 10px;
}

/* 发布入口按钮 */
.publish-entry {
  width: 100%;
  font-size: 15px;
  letter-spacing: 2px;
  font-weight: 700;
}

/* 筛选菜单 */
.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  color: #8a4a63;
  cursor: pointer;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background: #fff0f7;
  color: #e0539c;
}

.menu-item.active {
  background: linear-gradient(135deg, #ff9ec3, #f0619c);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 4px 10px rgba(240, 97, 156, 0.3);
}

/* ===== 右侧内容显示区：85%，可上下滚动 ===== */
.space-main {
  flex: 1;
  width: 85%;
  overflow-y: auto;
  padding: 20px 28px 40px;
  box-sizing: border-box;
  scroll-behavior: smooth;
}

.feed-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 18px;
}

.feed-head h2 {
  margin: 0;
  font-size: 26px;
  letter-spacing: 3px;
  color: #c2457c;
  text-shadow: 0 2px 10px rgba(255, 150, 195, 0.45);
}

.feed-count {
  font-size: 13px;
  color: #c78ca6;
}

.empty-tip {
  text-align: center;
  padding: 80px 0;
  color: #c78ca6;
}

.empty-emoji {
  font-size: 52px;
  animation: float 3s ease-in-out infinite;
}

.empty-tip p {
  margin-top: 12px;
  font-size: 15px;
  letter-spacing: 1px;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* 动态卡片 */
.moment-card {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #ffd9e8;
  border-radius: 18px;
  box-shadow: 0 8px 26px rgba(217, 93, 139, 0.13);
  padding: 18px 22px 10px;
  margin-bottom: 18px;
}

.moment-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.moment-avatar {
  flex-shrink: 0;
  background: linear-gradient(135deg, #ff9ec3, #f0619c);
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.moment-user {
  flex: 1;
  min-width: 0;
}

.moment-name {
  font-size: 15px;
  font-weight: 700;
  color: #b84f80;
  display: flex;
  align-items: center;
  gap: 6px;
}

.me-tag {
  font-size: 10px;
  font-weight: 400;
  color: #fff;
  background: #ff9ec3;
  border-radius: 8px;
  padding: 1px 6px;
}

/* 账号认证标识：超管王冠 / 管理员星星 */
.role-badge {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  margin-left: 2px;
  cursor: default;
}

.moment-time {
  font-size: 12px;
  color: #c78ca6;
  margin-top: 2px;
}

.moment-ops {
  flex-shrink: 0;
}

/* 删除入口：三点菜单 */
.moment-more {
  display: inline-block;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1;
  color: #c78ca6;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.moment-more:hover {
  color: #e0539c;
}

/* 卡片主体：点击打开详情 */
.moment-body {
  cursor: default;
}

.moment-img-cell {
  cursor: zoom-in;
}

.moment-content {
  margin: 14px 0 0;
  font-size: 15px;
  line-height: 1.9;
  color: #5f3d50;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 图片九宫格 */
.moment-images {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.moment-images.count-1 {
  grid-template-columns: minmax(0, 340px);
}

.moment-images.count-2,
.moment-images.count-4 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.moment-images.count-3,
.moment-images.count-5,
.moment-images.count-6,
.moment-images.count-7,
.moment-images.count-8,
.moment-images.count-9 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.moment-img {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(217, 93, 139, 0.12);
  display: block;
}

/* 操作栏 */
.moment-ops-bar {
  display: flex;
  align-items: center;
  gap: 22px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #ffd2e4;
}

.op-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #a06b83;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.op-item:hover {
  color: #e0539c;
}

.comment-toggle.active {
  color: #e0539c;
  font-weight: 700;
}

/* 查看详情：靠操作栏最右侧 */
.detail-link {
  margin-left: auto;
  color: #c9578b;
  font-weight: 700;
}

.expand-arrow {
  font-size: 10px;
  font-style: normal;
}

.like-btn.liked {
  color: #e0539c;
  font-weight: 700;
}

.op-icon {
  font-size: 14px;
}

/* 卡片内联评论区（点击💬向下展开） */
.card-comment-section {
  margin-top: 14px;
  padding: 12px 14px;
  background: #fff7fb;
  border: 1px dashed #ffc9e0;
  border-radius: 14px;
  animation: comment-slide 0.25s ease;
}

@keyframes comment-slide {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-comment-head {
  text-align: right;
  margin-bottom: 6px;
}

.comment-collapse {
  font-size: 12px;
  color: #c78ca6;
  cursor: pointer;
}

.comment-collapse:hover {
  color: #e0539c;
}

.feed-foot {
  text-align: center;
  padding: 14px 0 4px;
  font-size: 13px;
  letter-spacing: 2px;
  color: #c78ca6;
}

/* ===== 弹窗通用（粉色主题） ===== */
:deep(.pink-dialog) {
  border-radius: 18px;
  overflow: hidden;
}

:deep(.pink-dialog .el-dialog__title) {
  color: #c2457c;
  font-weight: 700;
  letter-spacing: 1px;
}

:deep(.pink-dialog .el-dialog__header) {
  background: linear-gradient(160deg, #fff2f8, #ffe6f0);
}

:deep(.pink-dialog .el-dialog__body) {
  background: #fffafd;
}

:deep(.pink-dialog .el-dialog__footer) {
  background: #fffafd;
}

/* ===== 发布弹窗 ===== */
.publish-dialog-body {
  padding: 4px 6px;
}

.publish-dialog-body :deep(.el-textarea__inner) {
  font-size: 14px;
  color: #5f3d50;
  border-radius: 10px;
  background: #fff;
}

.img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.img-item {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #ffd9e8;
}

.img-thumb {
  width: 100%;
  height: 100%;
}

.img-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  cursor: pointer;
}

.img-uploader {
  width: 76px;
  height: 76px;
}

.img-add {
  width: 76px;
  height: 76px;
  border: 1.5px dashed #ff9ec3;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #e0539c;
  font-size: 22px;
  cursor: pointer;
  background: #fff7fb;
  box-sizing: border-box;
}

.img-add span {
  font-size: 11px;
  margin-top: 2px;
}

/* ===== 详情弹窗 ===== */
.detail-body {
  padding: 2px 6px;
  max-height: 68vh;
  overflow-y: auto;
}

.detail-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-content {
  margin: 14px 0 0;
  font-size: 15px;
  line-height: 1.9;
  color: #5f3d50;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 14px;
}

.detail-img {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  cursor: zoom-in;
  box-shadow: 0 2px 8px rgba(217, 93, 139, 0.12);
}

.detail-ops {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 14px;
  padding: 10px 14px;
  background: #fff0f7;
  border-radius: 12px;
}

.detail-view {
  font-size: 13px;
  color: #a06b83;
}

.detail-like {
  font-size: 13px;
  color: #a06b83;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.detail-like:hover {
  color: #e0539c;
}

.detail-like.liked {
  color: #e0539c;
  font-weight: 700;
}

/* ===== 用户资料弹窗 ===== */
.clickable {
  cursor: pointer;
}

.moment-avatar.clickable {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.moment-avatar.clickable:hover {
  transform: scale(1.08);
  box-shadow: 0 4px 14px rgba(240, 97, 156, 0.5);
}

.moment-name.clickable {
  cursor: pointer;
}

.moment-name.clickable:hover {
  color: #e0539c;
  text-decoration: underline;
}

.profile-body {
  padding: 4px 6px;
}

.profile-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #ffd2e4;
}

.profile-avatar {
  flex-shrink: 0;
  background: linear-gradient(135deg, #ff9ec3, #f0619c);
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 4px 14px rgba(240, 97, 156, 0.35);
}

.profile-head-info {
  flex: 1;
  min-width: 0;
}

.profile-name {
  font-size: 20px;
  font-weight: 700;
  color: #b84f80;
  display: flex;
  align-items: center;
  gap: 6px;
}

.profile-no {
  font-size: 13px;
  color: #a06b83;
  margin: 4px 0 8px;
}

.profile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;
  margin: 16px 0;
}

.profile-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff7fb;
  border-radius: 10px;
  padding: 8px 12px;
  border: 1px solid #ffe4f0;
}

.p-label {
  font-size: 12px;
  color: #c78ca6;
}

.p-value {
  font-size: 13px;
  color: #5f3d50;
  font-weight: 600;
}

.p-value.coin {
  color: #e0539c;
}

/* 打赏区 */
.tip-section {
  margin-top: 8px;
  padding: 14px;
  background: linear-gradient(160deg, #fff0f7, #ffe6f0);
  border-radius: 14px;
  border: 1px solid #ffd0e5;
}

.tip-title {
  font-size: 15px;
  font-weight: 700;
  color: #c2457c;
}

.tip-balance {
  font-size: 12px;
  color: #a06b83;
  margin: 6px 0 10px;
}

.tip-quick {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.tip-chip {
  flex: 1;
  text-align: center;
  padding: 6px 0;
  font-size: 13px;
  color: #c2457c;
  background: #fff;
  border: 1px solid #ffc9e0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.tip-chip:hover {
  border-color: #f0619c;
}

.tip-chip.active {
  background: linear-gradient(135deg, #ff9ec3, #f0619c);
  color: #fff;
  font-weight: 700;
  border-color: transparent;
  box-shadow: 0 3px 8px rgba(240, 97, 156, 0.35);
}

.tip-btn {
  width: 100%;
  margin-top: 12px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 2px;
}

.self-tip-tip {
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: #c78ca6;
  padding: 14px 0;
}

.profile-loading {
  text-align: center;
  padding: 40px 0;
  color: #c78ca6;
  font-size: 14px;
}

/* ===== 我的便签墙弹窗 ===== */
.note-wall-body {
  padding: 4px 6px;
  max-height: 78vh;
  overflow-y: auto;
}

.note-editor {
  background: #fff7fb;
  border: 1px dashed #ffb6d5;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 18px;
}

.note-editor-title {
  font-size: 14px;
  font-weight: 700;
  color: #c2457c;
  margin-bottom: 10px;
}

.note-title-input {
  margin-bottom: 10px;
}

.note-editor :deep(.el-textarea__inner) {
  font-size: 14px;
  color: #5f3d50;
  border-radius: 10px;
  background: #fff;
  line-height: 1.8;
}

.note-editor-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.note-count-hint {
  font-size: 12px;
  color: #c78ca6;
}

.note-empty {
  text-align: center;
  padding: 40px 0 20px;
  color: #c78ca6;
}

.note-empty-emoji {
  font-size: 46px;
  margin-bottom: 10px;
}

.note-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.note-sticky {
  border: 1.5px solid;
  border-radius: 6px 6px 14px 14px;
  padding: 12px 14px 10px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 10px rgba(180, 100, 140, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.note-sticky:hover {
  transform: rotate(0deg) scale(1.03) !important;
  box-shadow: 0 8px 18px rgba(180, 100, 140, 0.28);
  z-index: 2;
}

.note-sticky-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed rgba(180, 100, 140, 0.25);
}

.note-sticky-title {
  font-size: 14px;
  font-weight: 700;
  color: #6b4457;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-sticky-del {
  cursor: pointer;
  color: #b0708a;
  font-size: 12px;
  padding: 0 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.note-sticky-del:hover {
  background: rgba(255, 100, 130, 0.15);
  color: #e0539c;
}

.note-sticky-content {
  flex: 1;
  font-size: 13px;
  line-height: 1.7;
  color: #5f3d50;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-sticky-time {
  margin-top: 8px;
  font-size: 11px;
  color: #a06b83;
  text-align: right;
}

/* 窄屏适配：改为上下布局 */
@media (max-width: 900px) {
  .space-layout {
    flex-direction: column;
    height: auto;
  }

  .space-side {
    width: 100%;
    min-width: 0;
    border-right: none;
    border-bottom: 1px solid rgba(255, 190, 220, 0.5);
    overflow-y: visible;
  }

  .space-main {
    width: 100%;
    overflow-y: visible;
    padding: 16px;
  }

  .space-page {
    height: auto;
    overflow: visible;
  }
}
</style>
